import * as React from "react";
import { useQuery, useMutation } from "convex/react";
import type { ColumnDef } from "@tanstack/react-table";
import { Check, Download, Mail, Pencil, Plus } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { DataTableFloatingBar } from "@/components/data-table/data-table-floating-bar";
import { InternalEntityDataTable } from "@/components/internal/shared/InternalEntityDataTable";
import { CreditKeyUserAccessButton } from "@/components/internal/credit-key/CreditKeyUserAccessButton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Card, CardContent } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/convexApi";
import { formatCurrency } from "@/lib/format";
import { getSalesRepBadgeClass } from "@/lib/sales-tag-colors";
import { cn } from "@/lib/utils";
import {
  exportToCSV,
  formatDateForExport,
  formatCurrencyForExport,
  type ExportColumn,
} from "@/lib/export";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
  getOrderSequenceBadgeClass,
  getOrderSequenceLabel,
  isDisplayableSequence,
} from "@/lib/customer-tag-colors";
import { matchesScopedFilter } from "./adminTabFilterUtils";

interface UsersTabProps {
  authUserId?: string;
}

interface UserRow {
  id: string;
  email: string;
  name: string;
  // Raw stored values, unlike `name` which is a display-only fallback chain.
  firstName: string;
  lastName: string;
  // Display-only: "N/A" when there is no company. Never send this to a mutation
  // — a customer whose company is literally "N/A" is indistinguishable from one
  // with none. Use `rawCompany` for anything that round-trips to the backend.
  company: string;
  rawCompany: string;
  isEnterprise: boolean;
  role: string;
  tier: string;
  phone: string;
  location: string;
  totalSpent: number;
  totalOrders: number;
  lastOrderDate: number | null;
  creditLimit: number;
  creditUsed: number;
  creditAvailable: number;
  paymentTerms: string;
  rep: string;
  rawSalesRepId: string;
  customerSince: number | null;
  createdAt: number;
  accountingEmail: string | null;
  orderingEmail: string | null;
  truckingEmail: string | null;
  sampleContext: {
    hasSamples: boolean;
    count: number;
    latestStatus: string | null;
    latestDate: number | null;
    latestSampleRequestId: string | null;
  };
  followUpContext: {
    openCount: number;
    openFollowUpId: string | null;
    doNotFollowUp: boolean;
    doNotFollowUpReason: string | null;
  };
}

function roleBadge(role: string) {
  const r = role.toLowerCase();
  if (r === "admin") return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  if (r === "sales") return "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200";
  if (r === "logistics")
    return "border-violet-300 bg-violet-100 text-violet-900 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-200";
  if (r === "accounting")
    return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  if (r === "inventory")
    return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
  return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
}

function tierBadge(tier: string) {
  const t = Number(tier);
  if (t === 1) return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  if (t === 2) return "border-slate-400 bg-slate-200 text-slate-900 dark:border-slate-600/60 dark:bg-slate-800/40 dark:text-slate-200";
  if (t === 3) return "border-orange-300 bg-orange-100 text-orange-900 dark:border-orange-800/60 dark:bg-orange-950/40 dark:text-orange-200";
  if (t >= 4) return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
  return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
}

function creditBadge(used: number, limit: number) {
  if (limit === 0) return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200";
  const ratio = used / limit;
  if (ratio >= 0.9) return "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200";
  if (ratio >= 0.7) return "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200";
  return "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200";
}

const ASSIGNABLE_REPS = [
  { code: "DS", label: "Daniel" },
  { code: "BLP", label: "Brian" },
  { code: "JEH", label: "Jon" },
  { code: "Sr", label: "Jeff" },
  { code: "KC", label: "Kris" },
  { code: "yes", label: "Needs Someone" },
  { code: "no", label: "Don't Need" },
] as const;

function RepAssignmentCell({
  row,
  authUserId,
}: {
  row: UserRow;
  authUserId: string | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const assignRep = useMutation(api.admin.assignSalesRep);

  const handleAssign = React.useCallback(
    async (code: string) => {
      if (!authUserId) return;
      setOpen(false);
      try {
        await assignRep({
          authUserId,
          userId: row.id as any,
          salesRepId: code,
        });
        toast.success(`Rep assigned to ${ASSIGNABLE_REPS.find((r) => r.code === code)?.label ?? code}`);
      } catch (err: any) {
        toast.error(err.message ?? "Failed to assign rep");
      }
    },
    [assignRep, authUserId, row.id],
  );

  if (!authUserId) {
    return (
      <Badge variant="outline" className={getSalesRepBadgeClass(row.rep)}>
        {row.rep}
      </Badge>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="cursor-pointer rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          <Badge variant="outline" className={cn(getSalesRepBadgeClass(row.rep), "cursor-pointer")}>
            {row.rep}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search reps..." />
          <CommandList className="max-h-full">
            <CommandEmpty>No reps found.</CommandEmpty>
            <CommandGroup className="max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden">
              {ASSIGNABLE_REPS.map((rep) => {
                const isCurrentRep = row.rep === rep.label;
                return (
                  <CommandItem
                    key={rep.code}
                    onSelect={() => handleAssign(rep.code)}
                  >
                    <div
                      className={cn(
                        "flex size-4 items-center justify-center rounded-sm border border-primary",
                        isCurrentRep
                          ? "bg-primary"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check />
                    </div>
                    <Badge variant="outline" className={cn(getSalesRepBadgeClass(rep.label), "text-xs")}>
                      {rep.label}
                    </Badge>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ContactEmailsEditCell({
  row,
  authUserId,
}: {
  row: UserRow;
  authUserId: string | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const [accountingEmail, setAccountingEmail] = React.useState(row.accountingEmail ?? "");
  const [orderingEmail, setOrderingEmail] = React.useState(row.orderingEmail ?? "");
  const [truckingEmail, setTruckingEmail] = React.useState(row.truckingEmail ?? "");
  const [saving, setSaving] = React.useState(false);
  const updateContactEmails = useMutation(api.admin.updateUserContactEmails);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setAccountingEmail(row.accountingEmail ?? "");
      setOrderingEmail(row.orderingEmail ?? "");
      setTruckingEmail(row.truckingEmail ?? "");
    }
    setOpen(isOpen);
  };

  const handleSave = async () => {
    if (!authUserId) return;
    setSaving(true);
    try {
      await updateContactEmails({
        authUserId,
        userId: row.id as any,
        accountingEmail: accountingEmail.trim() || null,
        orderingEmail: orderingEmail.trim() || null,
        truckingEmail: truckingEmail.trim() || null,
      });
      toast.success("Contact emails updated");
      setOpen(false);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update emails");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
          <Mail className="mr-1 h-3 w-3" />
          Emails
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-3">
          <p className="text-sm font-medium">Contact Email Routing</p>
          <div className="space-y-1">
            <Label htmlFor={`accounting-${row.id}`} className="text-xs">Accounting Email</Label>
            <Input
              id={`accounting-${row.id}`}
              type="email"
              value={accountingEmail}
              onChange={(e) => setAccountingEmail(e.target.value)}
              placeholder="e.g. billing@roaster.com"
              className="h-8 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">Optional. If set, used for invoice emails instead of primary email.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`ordering-${row.id}`} className="text-xs">Ordering Email</Label>
            <Input
              id={`ordering-${row.id}`}
              type="email"
              value={orderingEmail}
              onChange={(e) => setOrderingEmail(e.target.value)}
              placeholder="e.g. orders@roaster.com"
              className="h-8 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">Optional. If set, used for order confirmations instead of primary email.</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`trucking-${row.id}`} className="text-xs">Trucking Email</Label>
            <Input
              id={`trucking-${row.id}`}
              type="email"
              value={truckingEmail}
              onChange={(e) => setTruckingEmail(e.target.value)}
              placeholder="e.g. shipping@roaster.com"
              className="h-8 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">Optional. If set, used for shipping/logistics updates instead of primary email.</p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs">
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="h-7 text-xs">
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function CompanyEditCell({
  row,
  authUserId,
}: {
  row: UserRow;
  authUserId: string | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const [company, setCompany] = React.useState(row.rawCompany);
  const [saving, setSaving] = React.useState(false);
  const updateCompany = useMutation(api.admin.updateUserCompany);

  // `row` is backed by a live Convex query, so reading it at save time would
  // yield whatever is currently in the database. Snapshot on open so the guard
  // compares against the value this admin actually saw.
  const openedWithCompany = React.useRef(row.rawCompany);

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      openedWithCompany.current = row.rawCompany;
      setCompany(row.rawCompany);
    }
    setOpen(isOpen);
  };

  const handleSave = async () => {
    if (!authUserId) return;
    setSaving(true);
    try {
      const result = await updateCompany({
        authUserId,
        userId: row.id as any,
        company: company.trim() || null,
        expectedCurrentCompany: openedWithCompany.current || null,
      });
      toast.success(
        result?.changed ? "Company name updated" : "Company name unchanged",
      );
      setOpen(false);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update company name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 shrink-0 p-0 opacity-0 group-hover/company:opacity-100 focus-visible:opacity-100"
          aria-label="Edit company name"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-3">
          <p className="text-sm font-medium">Company Name</p>
          <div className="space-y-1">
            <Label htmlFor={`company-${row.id}`} className="text-xs">
              Company
            </Label>
            <Input
              id={`company-${row.id}`}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g. Anthem Coffee Roasters"
              className="h-8 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              Leave blank to clear. Orders, invoices, and BOLs read this value
              live, so they will show the new name immediately.
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-7 text-xs"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(false)}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NameEditCell({
  row,
  authUserId,
}: {
  row: UserRow;
  authUserId: string | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const [firstName, setFirstName] = React.useState(row.firstName);
  const [lastName, setLastName] = React.useState(row.lastName);
  const [saving, setSaving] = React.useState(false);
  const updateName = useMutation(api.admin.updateUserName);

  // `row` is backed by a live Convex query, so reading it at save time would
  // yield whatever is currently in the database. Snapshot on open so the guard
  // compares against the values this admin actually saw.
  const openedWith = React.useRef({
    firstName: row.firstName,
    lastName: row.lastName,
  });

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      openedWith.current = { firstName: row.firstName, lastName: row.lastName };
      setFirstName(row.firstName);
      setLastName(row.lastName);
    }
    setOpen(isOpen);
  };

  const handleSave = async () => {
    if (!authUserId) return;
    setSaving(true);
    try {
      const result = await updateName({
        authUserId,
        userId: row.id as any,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
        expectedCurrentFirstName: openedWith.current.firstName || null,
        expectedCurrentLastName: openedWith.current.lastName || null,
      });
      toast.success(
        result?.changed ? "Customer name updated" : "Customer name unchanged",
      );
      setOpen(false);
    } catch (err: any) {
      toast.error(err.message ?? "Failed to update customer name");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-5 w-5 shrink-0 p-0 opacity-0 group-hover/name:opacity-100 focus-visible:opacity-100"
          aria-label="Edit customer name"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-3">
          <p className="text-sm font-medium">Customer Name</p>
          <div className="space-y-1">
            <Label htmlFor={`first-name-${row.id}`} className="text-xs">
              First Name
            </Label>
            <Input
              id={`first-name-${row.id}`}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="e.g. Aaron"
              className="h-8 text-xs"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`last-name-${row.id}`} className="text-xs">
              Last Name
            </Label>
            <Input
              id={`last-name-${row.id}`}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="e.g. Davenport"
              className="h-8 text-xs"
            />
            <p className="text-[10px] text-muted-foreground">
              At least one name is required. The customer's display name is
              rebuilt from these two fields.
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="h-7 text-xs"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(false)}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function formatDateShort(ts: number | null): string {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function ManualFollowUpButton({
  row,
  authUserId,
}: {
  row: UserRow;
  authUserId: string | undefined;
}) {
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState("");
  const [date, setDate] = React.useState(() => new Date().toISOString().slice(0, 10));
  const createManualFollowUp = useMutation((api as any).salesFollowUps.createManualFollowUp);

  const handleCreate = async () => {
    if (!authUserId) return;
    const dueAt = new Date(`${date}T12:00:00`).getTime();
    if (!Number.isFinite(dueAt)) {
      toast.error("Choose a due date");
      return;
    }
    try {
      await createManualFollowUp({
        authUserId,
        customerId: row.id as any,
        dueAt,
        note,
      });
      toast.success("Follow-up created");
      setOpen(false);
      setNote("");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to create follow-up");
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setOpen(true)}>
        <Plus className="mr-1 h-3 w-3" />
        Follow-up
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manual follow-up</DialogTitle>
            <DialogDescription>{row.company !== "N/A" ? row.company : row.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor={`follow-up-date-${row.id}`}>Due date</Label>
              <Input
                id={`follow-up-date-${row.id}`}
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`follow-up-note-${row.id}`}>Note</Label>
              <Textarea
                id={`follow-up-note-${row.id}`}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SamplesContextCell({
  row,
  authUserId,
}: {
  row: UserRow;
  authUserId: string | undefined;
}) {
  const sample = row.sampleContext;
  const followUp = row.followUpContext;
  return (
    <div className="min-w-[190px] space-y-1.5">
      <div className="flex flex-wrap items-center gap-1">
        <Badge variant="outline" className={sample.hasSamples ? "border-sky-300 bg-sky-100 text-sky-900" : "border-zinc-300 bg-zinc-100 text-zinc-900"}>
          {sample.hasSamples ? `${sample.count} sample${sample.count === 1 ? "" : "s"}` : "No samples"}
        </Badge>
        {sample.latestStatus ? (
          <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-800">
            {sample.latestStatus}
          </Badge>
        ) : null}
        {followUp.doNotFollowUp ? (
          <Badge variant="outline" className="border-red-300 bg-red-100 text-red-900">
            Suppressed
          </Badge>
        ) : null}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {sample.latestDate ? `Latest ${formatDateShort(sample.latestDate)}` : "No sample history"}
        {followUp.openCount > 0 ? ` - ${followUp.openCount} open follow-up${followUp.openCount === 1 ? "" : "s"}` : ""}
      </p>
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <Link className="underline-offset-2 hover:underline" to={`/sales/samples?customerId=${encodeURIComponent(row.id)}`}>
          Samples
        </Link>
        <Link className="underline-offset-2 hover:underline" to={`/sales/crm/follow-ups?customerId=${encodeURIComponent(row.id)}`}>
          Follow-ups
        </Link>
        <ManualFollowUpButton row={row} authUserId={authUserId} />
      </div>
    </div>
  );
}

function useColumns(data: UserRow[], authUserId?: string) {
  const repOptions = React.useMemo(() => {
    const reps = [...new Set(data.map((r) => r.rep).filter(Boolean))].sort();
    return reps.map((r) => ({ label: r, value: r }));
  }, [data]);

  return React.useMemo<ColumnDef<UserRow>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Customer" />
        ),
        cell: ({ row }) => {
          const hasCompany =
            Boolean(row.original.company) && row.original.company !== "N/A";
          // The name only gets its own line when it says something the company
          // line doesn't. When it doesn't render, the name pencil moves up to
          // the company line so the name stays editable either way.
          const showNameLine =
            hasCompany && row.original.company !== row.original.name;
          return (
            <div className="flex flex-col">
              <div className="group/company group/name flex items-center gap-1">
                <span className="text-xs font-medium">
                  {hasCompany ? row.original.company : row.original.name}
                </span>
                <CompanyEditCell row={row.original} authUserId={authUserId} />
                {!showNameLine && (
                  <NameEditCell row={row.original} authUserId={authUserId} />
                )}
              </div>
              {showNameLine && (
                <div className="group/name flex items-center gap-1">
                  <span className="text-xs text-muted-foreground">
                    {row.original.name}
                  </span>
                  <NameEditCell row={row.original} authUserId={authUserId} />
                </div>
              )}
              <CreditKeyUserAccessButton customerId={row.original.id} customerName={hasCompany ? row.original.company : row.original.name} role={row.original.role} />
            </div>
          );
        },
        filterFn: (row, _columnId, filterValue) => {
          if (!filterValue) return true;
          const search = String(filterValue).toLowerCase();
          const name = (row.original.name ?? "").toLowerCase();
          const company = (row.original.company ?? "").toLowerCase();
          return name.includes(search) || company.includes(search);
        },
        meta: {
          label: "Customer",
          placeholder: "Search name...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "email",
        accessorKey: "email",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Email" />
        ),
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.email}</span>
        ),
        meta: {
          label: "Email",
          placeholder: "Search email...",
          variant: "text" as const,
        },
        enableColumnFilter: true,
      },
      {
        id: "role",
        accessorKey: "role",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Role" />
        ),
        cell: ({ row }) => (
          <Badge variant="outline" className={roleBadge(row.original.role)}>
            {row.original.role}
          </Badge>
        ),
        meta: {
          label: "Role",
          variant: "select" as const,
          options: [
            { label: "User", value: "user" },
            { label: "Admin", value: "admin" },
            { label: "Sales", value: "sales" },
            { label: "Logistics", value: "logistics" },
            { label: "Accounting", value: "accounting" },
            { label: "Inventory", value: "inventory" },
          ],
        },
        enableColumnFilter: true,
      },
      {
        id: "isEnterprise",
        accessorKey: "isEnterprise",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Enterprise" />
        ),
        cell: ({ row }) => (
          <Badge
            variant="outline"
            className={getEnterpriseBadgeClass(row.original.isEnterprise)}
          >
            {getEnterpriseBadgeLabel(row.original.isEnterprise)}
          </Badge>
        ),
        meta: {
          label: "Enterprise",
          variant: "select" as const,
          options: [
            { label: "Enterprise", value: "true" },
            { label: "Standard", value: "false" },
          ],
        },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some(
            (value: string) =>
              value === String(Boolean(row.original.isEnterprise)),
          ),
      },
      {
        id: "rep",
        accessorKey: "rep",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Rep" />
        ),
        cell: ({ row }) => (
          <RepAssignmentCell row={row.original} authUserId={authUserId} />
        ),
        meta: { label: "Rep", variant: "select" as const, options: repOptions },
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValues: string[]) =>
          filterValues.some((v) => v === row.original.rep),
      },
      {
        id: "contactEmails",
        header: "Email Routing",
        cell: ({ row }) => (
          <ContactEmailsEditCell row={row.original} authUserId={authUserId} />
        ),
        meta: { label: "Email Routing" },
        enableColumnFilter: false,
      },
      {
        id: "samples",
        accessorFn: (row) => row.sampleContext.count,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Samples" />
        ),
        cell: ({ row }) => (
          <SamplesContextCell row={row.original} authUserId={authUserId} />
        ),
        meta: { label: "Samples", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "tier",
        accessorKey: "tier",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Tier" />
        ),
        cell: ({ row }) => (
          <Badge variant="outline" className={tierBadge(row.original.tier)}>
            {row.original.tier === "0" ? "-" : row.original.tier}
          </Badge>
        ),
        meta: {
          label: "Tier",
          variant: "text" as const,
          placeholder: "Filter tier...",
        },
        enableColumnFilter: true,
      },
      {
        id: "totalOrders",
        accessorKey: "totalOrders",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Orders" />
        ),
        cell: ({ row }) => {
          const count = row.original.totalOrders;
          return (
            <div className="flex items-center gap-1.5">
              <span className="text-xs tabular-nums">{count}</span>
              {isDisplayableSequence(count) && (
                <span className={`inline-flex items-center rounded border text-[10px] font-medium py-0 px-1.5 animate-badge-shine ${getOrderSequenceBadgeClass(count)}`}>
                  {getOrderSequenceLabel(count)}
                </span>
              )}
            </div>
          );
        },
        meta: { label: "Orders", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "totalSpent",
        accessorKey: "totalSpent",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Total Spent" />
        ),
        cell: ({ row }) => (
          <span className="text-xs font-medium tabular-nums">
            {formatCurrency(row.original.totalSpent)}
          </span>
        ),
        meta: { label: "Total Spent", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "creditLimit",
        accessorKey: "creditLimit",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Credit Limit" />
        ),
        cell: ({ row }) => {
          if (row.original.creditLimit === 0) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }
          return (
            <span className="text-xs tabular-nums">
              {formatCurrency(row.original.creditLimit)}
            </span>
          );
        },
        meta: { label: "Credit Limit", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "creditUsed",
        accessorKey: "creditUsed",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Credit Used" />
        ),
        cell: ({ row }) => {
          if (row.original.creditLimit === 0 && row.original.creditUsed === 0) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }
          return (
            <Badge variant="outline" className={creditBadge(row.original.creditUsed, row.original.creditLimit)}>
              {formatCurrency(row.original.creditUsed)}
            </Badge>
          );
        },
        meta: { label: "Credit Used", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "creditAvailable",
        accessorKey: "creditAvailable",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Credit Avail" />
        ),
        cell: ({ row }) => {
          if (row.original.creditLimit === 0) {
            return <span className="text-xs text-muted-foreground">-</span>;
          }
          return (
            <span className="text-xs tabular-nums">
              {formatCurrency(row.original.creditAvailable)}
            </span>
          );
        },
        meta: { label: "Credit Available", variant: "number" as const },
        enableColumnFilter: true,
      },
      {
        id: "paymentTerms",
        accessorKey: "paymentTerms",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Terms" />
        ),
        cell: ({ row }) => (
          <span className="text-xs">{row.original.paymentTerms}</span>
        ),
        meta: { label: "Terms", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "phone",
        accessorKey: "phone",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Phone" />
        ),
        cell: ({ row }) => (
          <span className="text-xs">{row.original.phone}</span>
        ),
        meta: { label: "Phone", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "location",
        accessorKey: "location",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Location" />
        ),
        cell: ({ row }) => (
          <span className="text-xs">{row.original.location}</span>
        ),
        meta: {
          label: "Location",
          variant: "text" as const,
          placeholder: "Search location...",
        },
        enableColumnFilter: true,
      },
      {
        id: "lastOrderDate",
        accessorKey: "lastOrderDate",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Last Order" />
        ),
        cell: ({ row }) => {
          const d = formatDateShort(row.original.lastOrderDate);
          if (!d) return <span className="text-xs text-muted-foreground">-</span>;
          return <span className="text-xs">{d}</span>;
        },
        meta: { label: "Last Order", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "customerSince",
        accessorKey: "customerSince",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Customer Since" />
        ),
        cell: ({ row }) => {
          const d = formatDateShort(row.original.customerSince);
          if (!d) return <span className="text-xs text-muted-foreground">-</span>;
          return <span className="text-xs">{d}</span>;
        },
        meta: { label: "Customer Since", variant: "text" as const },
        enableColumnFilter: true,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label="Created" />
        ),
        cell: ({ row }) => {
          const d = formatDateShort(row.original.createdAt);
          if (!d) return <span className="text-xs text-muted-foreground">-</span>;
          return <span className="text-xs">{d}</span>;
        },
        meta: { label: "Created", variant: "date" as const },
        enableColumnFilter: true,
      },
    ],
    [authUserId, repOptions],
  );
}

export function UsersTab({ authUserId }: UsersTabProps) {
  const [searchParams] = useSearchParams();
  const usersData = useQuery(
    api.admin.getAllUsers,
    authUserId ? { authUserId } : "skip",
  );

  const isLoading = usersData === undefined;

  const tableData = React.useMemo((): UserRow[] => {
    if (!usersData || !Array.isArray(usersData)) return [];
    return (usersData as any[]).map((user: any, index: number) => {
      const createdAt = user.createdAt || user._creationTime || 0;
      const location =
        [user.city, user.state].filter(Boolean).join(", ") || "N/A";
      const paymentTermsText = user.paymentTerms
        ? `Net ${user.paymentTerms}`
        : "N/A";
      const creditLimit = user.creditLimit || 0;
      const creditUsed = user.creditUsed || 0;
      const creditAvailable = Math.max(0, creditLimit - creditUsed);

      return {
        id: user._id || `user-${index}`,
        email: user.authEmail || user.email || "N/A",
        name:
          user.authName ||
          user.name ||
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          "N/A",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        company: user.company || "N/A",
        rawCompany: user.company ?? "",
        isEnterprise: Boolean(user.isEnterprise),
        role: user.role || "user",
        tier: user.tier?.toString() || "0",
        phone: user.mainPhoneNumber || "N/A",
        location,
        totalSpent: typeof user.totalSpent === "number" ? user.totalSpent : 0,
        totalOrders: user.totalOrders || 0,
        lastOrderDate: user.lastOrderDate || null,
        creditLimit,
        creditUsed,
        creditAvailable,
        paymentTerms: paymentTermsText,
        rep: user.salesRepName || "Unassigned",
        rawSalesRepId: user.salesRepId ?? "",
        customerSince: user.customerSince || null,
        createdAt,
        accountingEmail: user.accountingEmail ?? null,
        orderingEmail: user.orderingEmail ?? null,
        truckingEmail: user.truckingEmail ?? null,
        sampleContext: user.sampleContext ?? {
          hasSamples: false,
          count: 0,
          latestStatus: null,
          latestDate: null,
          latestSampleRequestId: null,
        },
        followUpContext: user.followUpContext ?? {
          openCount: 0,
          openFollowUpId: null,
          doNotFollowUp: false,
          doNotFollowUpReason: null,
        },
      };
    });
  }, [usersData]);

  const emailParam = searchParams.get("email");
  const companyParam = searchParams.get("company");
  const userIdParam = searchParams.get("userId");

  const scopedTableData = React.useMemo(
    () =>
      tableData.filter((row) => {
        if (!matchesScopedFilter([row.id], userIdParam)) return false;
        if (!matchesScopedFilter([row.email], emailParam)) return false;
        if (!matchesScopedFilter([row.company, row.name], companyParam)) return false;
        return true;
      }),
    [tableData, userIdParam, emailParam, companyParam],
  );

  const columns = useColumns(scopedTableData, authUserId);

  const exportColumns = React.useMemo<ExportColumn[]>(
    () => [
      { key: "email", label: "Email" },
      { key: "name", label: "Name" },
      { key: "company", label: "Company" },
      {
        key: "isEnterprise",
        label: "Enterprise",
        format: (v: unknown) => getEnterpriseBadgeLabel(v),
      },
      { key: "role", label: "Role" },
      { key: "rep", label: "Sales Rep" },
      { key: "tier", label: "Tier" },
      { key: "phone", label: "Phone" },
      { key: "location", label: "Location" },
      { key: "totalOrders", label: "Total Orders" },
      { key: "totalSpent", label: "Total Spent", format: formatCurrencyForExport },
      { key: "creditLimit", label: "Credit Limit", format: formatCurrencyForExport },
      { key: "creditUsed", label: "Credit Used", format: formatCurrencyForExport },
      { key: "creditAvailable", label: "Credit Available", format: formatCurrencyForExport },
      { key: "paymentTerms", label: "Payment Terms" },
      {
        key: "lastOrderDate",
        label: "Last Order",
        format: formatDateForExport,
      },
      {
        key: "customerSince",
        label: "Customer Since",
        format: formatDateForExport,
      },
      { key: "createdAt", label: "Created", format: formatDateForExport },
    ],
    [],
  );

  const exportRows = React.useCallback(
    (rows: UserRow[], filename: string) => {
      if (rows.length === 0) {
        toast.error("No users selected");
        return;
      }
      exportToCSV(rows, exportColumns, filename);
    },
    [exportColumns],
  );

  const handleExport = React.useCallback(() => {
    exportRows(
      scopedTableData,
      `users-${new Date().toISOString().split("T")[0]}.csv`,
    );
  }, [exportRows, scopedTableData]);

  const handleExportSelected = React.useCallback(
    (selectedRows: UserRow[]) => {
      exportRows(
        selectedRows,
        `users-selected-${new Date().toISOString().split("T")[0]}.csv`,
      );
    },
    [exportRows],
  );

  return (
    <div>
      <Card>
        <CardContent className="pt-6">
          <div className="mb-4">
            <h2 className="text-xs font-semibold">Users</h2>
            <p className="text-xs text-muted-foreground">
              Internal and customer accounts with role-based access.
            </p>
          </div>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-96 w-full" />
            </div>
          ) : scopedTableData.length > 0 ? (
            <>
              <div className="mb-2 flex items-center justify-end">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="mr-2 h-4 w-4" />
                  Export All
                </Button>
              </div>
              <InternalEntityDataTable
                data={scopedTableData}
                columns={columns}
                pageSize={20}
                initialSorting={[{ id: "totalSpent", desc: true }]}
                actionBar={({ selectedRows, clearSelection }) => (
                  <DataTableFloatingBar
                    selectedCount={selectedRows.length}
                    onClear={clearSelection}
                  >
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleExportSelected(selectedRows)}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" />
                      Export Selected
                    </Button>
                  </DataTableFloatingBar>
                )}
              />
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              No users found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
